import { coachQualifiesForPgaMemberTag } from '@lib/pga-coach/coach-credentials'
import pluralize from '@lib/pluralize'

export function countVerifiedPgaMembersFromHits(hits = []) {
  return hits.filter((hit) => coachQualifiesForPgaMemberTag(hit.memberType)).length
}

function isFacetValueMap(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function facetDataFromList(facets, attribute) {
  if (!Array.isArray(facets)) return null
  const facet = facets.find((entry) => entry?.name === attribute)
  return isFacetValueMap(facet?.data) ? facet.data : null
}

/**
 * InstantSearch's helper stores facet counts as `{ name, data }` arrays,
 * not the raw Algolia map `{ memberType: { MB: n } }`. Prefer the main
 * query (`_rawResults[0]` / conjunctive `facets`) so the certified total
 * matches `nbHits`. Skip `disjunctiveFacets`: that extra query omits the
 * current memberType refinement.
 */
export function getMemberTypeFacetCounts(searchResults) {
  if (!searchResults) return null

  const fromMap = searchResults.facets?.memberType
  if (isFacetValueMap(fromMap)) return fromMap

  const fromRaw = searchResults._rawResults?.[0]?.facets?.memberType
  if (isFacetValueMap(fromRaw)) return fromRaw

  const fromHelper = facetDataFromList(searchResults.facets, 'memberType')
  if (fromHelper) return fromHelper

  return null
}

export function countVerifiedPgaMembersFromFacets(searchResults) {
  const memberTypeFacet = getMemberTypeFacetCounts(searchResults)
  if (!memberTypeFacet) return null

  return Object.entries(memberTypeFacet).reduce((sum, [memberType, count]) => (
    coachQualifiesForPgaMemberTag(memberType) ? sum + Number(count) : sum
  ), 0)
}

export function formatDirectoryPlaceName(placeName) {
  if (typeof placeName !== 'string') return ''
  const trimmed = placeName.trim()
  return trimmed.includes(',') ? trimmed : ''
}

export function formatResultsCountParts(coachCount, verifiedCount, placeName) {
  if (!coachCount || coachCount <= 0) return null
  const noun = pluralize(coachCount, 'coach', undefined, 'coaches')
  const location = formatDirectoryPlaceName(placeName)
  const coaches = location
    ? `${coachCount} ${noun} near ${location}`
    : `${coachCount} ${noun} available`
  const members = Number(verifiedCount) || 0
  const verb = members === 1 ? 'is' : 'are'
  return {
    prefix: `${coaches}, ${members} ${verb} certified `,
    membershipLabel: `PGA of America ${pluralize(members, 'Member', undefined, 'Members')}`,
  }
}

export function formatResultsCountLine(coachCount, verifiedCount, placeName) {
  const parts = formatResultsCountParts(coachCount, verifiedCount, placeName)
  return parts ? `${parts.prefix}${parts.membershipLabel}` : null
}

export function getResultsCountLine({
  displayedCoachCount = 0,
  hits = [],
  searchResults,
  savedOnly = false,
  isCoachSaved,
  placeName,
} = {}) {
  const visibleHits = savedOnly && typeof isCoachSaved === 'function'
    ? hits.filter((hit) => isCoachSaved(hit.objectID))
    : hits

  const verifiedCount = savedOnly
    ? countVerifiedPgaMembersFromHits(visibleHits)
    : countVerifiedPgaMembersFromFacets(searchResults)
      ?? countVerifiedPgaMembersFromHits(visibleHits)

  return formatResultsCountParts(displayedCoachCount, verifiedCount, placeName)
}
